import { useAuthentication } from '@hooks/authentication';
import { usePayment } from '@hooks/payment';
import { ProductSelectedOptionItem } from '@models/commerce';
import { ProductModel, QuestionOptionEditModel } from '@models/product';
import {
  CalendarCartLine,
  convertCalendarCartToCheckoutPayload,
  isCalendarOptionProduct,
} from '@utils/product-option-date';
import { getModalQuestionOptions } from '@utils/preferred-schedule';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import AppServiceCautionsModal from './AppServiceCautionsModal';
import { ClassInquiryModal } from './ClassInquiryModal';
import { CommerceOptionModal } from './CommerceOptionModal';
import { CommercePaymentModal } from './CommercePaymentModal';
import { ProductCalendarCartSheet } from './ProductCalendarCartSheet';
import { ProductDetailContentsView } from './ProductDetailContentsView';
import './ProductDetailView.scss';
import { RequestReopenAlarmModal } from './RequestReopenAlarmModal';

const HEADER_HEIGHT = '56px';

export const ProductDetailView: React.FC<{ product: ProductModel }> = (props) => {
  const { pathname } = useLocation();
  const { authStore } = useAuthentication();
  const payment = usePayment();
  const [headerOnImage, setHeaderOnImage] = useState<boolean>(true);
  const [showRequestChat, setShowRequestChat] = useState<boolean>(false);
  const [showRequestReopenAlarm, setShowRequestReopenAlarm] = useState<boolean>(false);
  const [scrollFocusOn, setScrollFocusOn] = useState<string>('detail');
  const [showOptionModal, setShowOptionModal] = useState<boolean>(false);
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  const [totalFee, setTotalFee] = useState<number>(0);
  const [selectedOptions, setSelectedOptions] = useState<ProductSelectedOptionItem[]>([]);
  const [showCautions, setShowCautions] = useState<boolean>(false);
  const [calendarCartLines, setCalendarCartLines] = useState<CalendarCartLine[]>([]);
  const [showCalendarCartSheet, setShowCalendarCartSheet] = useState<boolean>(false);
  const [qnaOptions, setQnaOptions] = useState<QuestionOptionEditModel[]>([]);
  const productDetailInfoRef = useRef<any>();
  const productEtcInfoRef = useRef<any>();
  const scrollTopRef = useRef<any>();

  const data = props.product;
  const isCalendarProduct = useMemo(
    () =>
      data?.saleType === 'commerce' &&
      isCalendarOptionProduct(data.productOptions, { forceOptionModal: data.forceOptionModal }),
    [data?.saleType, data?.productOptions, data?.forceOptionModal],
  );
  const calendarCartCount = calendarCartLines.reduce((sum, line) => sum + line.optionCount, 0);
  const calendarCartTotalFee = calendarCartLines.reduce((sum, line) => sum + line.lineTotal, 0);

  useEffect(() => {
    if (scrollTopRef.current) {
      scrollTopRef.current.scrollIntoView({ block: 'start' });
    }
  }, [pathname]);

  useEffect(() => {
    setCalendarCartLines([]);
    setShowCalendarCartSheet(false);
    setQnaOptions([]);
  }, [data?._id]);

  useEffect(() => {
    document.title = data?.title ? `아이고고 | ${data.title}` : '아이고고';
    return () => {
      document.title = '아이고고 - 우리아이 맞춤 키즈클래스 플랫폼';
    };
  }, [data?.title]);

  const openCalendarCheckout = () => {
    if (calendarCartLines.length === 0) {
      return;
    }

    payment.clear();
    const payload = convertCalendarCartToCheckoutPayload(calendarCartLines);
    setSelectedOptions(payload.selectedOptions);
    setTotalFee(payload.totalFee);
    setQnaOptions(
      getModalQuestionOptions(data?.questionOptions).map((item) => ({
        question: item.question,
        questionType: item.questionType,
        preferredSchedule: item.preferredSchedule,
        answer: '',
      })),
    );
    setShowCalendarCartSheet(false);
    setShowPaymentModal(true);
  };

  return (
    <div style={{ position: 'relative', height: '100%' }}>
      <AppServiceCautionsModal
        open={showCautions}
        onCancel={() => {
          setShowCautions(false);
        }}></AppServiceCautionsModal>

      <RequestReopenAlarmModal
        open={showRequestReopenAlarm}
        productId={data._id}
        user={authStore.user}
        onCancel={() => {
          setShowRequestReopenAlarm(false);
        }}></RequestReopenAlarmModal>
      <ClassInquiryModal
        childList={authStore.user?.profile?.children}
        open={showRequestChat}
        onCancel={() => {
          setShowRequestChat(false);
        }}
        onConfirm={() => {
          setShowRequestChat(false);
        }}></ClassInquiryModal>
      <CommerceOptionModal
        open={showOptionModal}
        product={data}
        onChangeTotalFee={(value) => {
          setTotalFee(value);
        }}
        onChangeSelectedOptions={(options) => {
          setSelectedOptions(options);
        }}
        onCancel={() => {
          setShowOptionModal(false);
        }}
        onConfirm={() => {
          payment.clear();
          setShowOptionModal(false);
          setShowPaymentModal(true);
        }}></CommerceOptionModal>
      <ProductCalendarCartSheet
        open={showCalendarCartSheet}
        lines={calendarCartLines}
        onClose={() => setShowCalendarCartSheet(false)}
        onRemove={(id) => {
          setCalendarCartLines((prev) => {
            const next = prev.filter((line) => line.id !== id);
            if (next.length === 0) {
              setShowCalendarCartSheet(false);
            }
            return next;
          });
        }}
        onPurchase={openCalendarCheckout}
      />
      <CommercePaymentModal
        open={showPaymentModal}
        selectedOptions={selectedOptions}
        qnaOptions={qnaOptions}
        collectQnaInPayment={isCalendarProduct}
        onChangeQnaOptions={setQnaOptions}
        onClickBack={() => {
          setShowPaymentModal(false);
          if (isCalendarProduct) {
            setShowCalendarCartSheet(calendarCartLines.length > 0);
          } else {
            setShowOptionModal(true);
          }
        }}
        onConfirm={() => {
          setShowPaymentModal(false);
          if (isCalendarProduct) {
            setCalendarCartLines([]);
          }
        }}
        product={data}
        totalFee={totalFee}></CommercePaymentModal>
      <div
        style={{
          position: 'sticky',
          zIndex: 1,
          top: 0,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          height: HEADER_HEIGHT,
          backgroundColor: headerOnImage ? 'transparent' : '#ffffff',
          borderBottom: headerOnImage ? 'none' : '1px solid #eff1f4',
        }}></div>
      <div style={{ marginTop: '-56px', height: 'calc(100dvh - 44px)', overflow: 'hidden' }}>
        <ProductDetailContentsView
          data={data}
          isCalendarOptionProduct={isCalendarProduct}
          calendarCartCount={calendarCartCount}
          calendarCartTotalFee={calendarCartTotalFee}
          onAddCalendarCartLine={(line) => {
            setCalendarCartLines((prev) => [...prev, line]);
            setShowCalendarCartSheet(false);
          }}
          onOpenCalendarCartSheet={() => {
            if (calendarCartLines.length > 0) {
              setShowCalendarCartSheet(true);
            }
          }}
          onPurchaseCalendarCart={openCalendarCheckout}
          setHeaderOnImage={setHeaderOnImage}
          scrollFocusOn={scrollFocusOn}
          setScrollFocusOn={setScrollFocusOn}
          setShowRequestChat={setShowRequestChat}
          setShowRequestReopenAlarm={setShowRequestReopenAlarm}
          setShowCautions={setShowCautions}
          setShowOptionModal={setShowOptionModal}
          payment={payment}
          scrollTopRef={scrollTopRef}
          productDetailInfoRef={productDetailInfoRef}
          productEtcInfoRef={productEtcInfoRef}
        />
      </div>
    </div>
  );
};

export default ProductDetailView;
